import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../Utils/ApiConfig';

const SetNewPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract token, type, and email from URL query parameters
  const [token, setToken] = useState('');
  const [type, setType] = useState('');
  const [email, setEmail] = useState('');
  
  // Set document title
  useEffect(() => {
    document.title = "Set New Password";
  }, []);
  
  // Form states
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI states
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  // Extract token, type, and email from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const extractedToken = queryParams.get('token');
    const extractedType = queryParams.get('type');
    const extractedEmail = queryParams.get('email');
    
    if (extractedToken && extractedType) {
      setToken(extractedToken);
      setType(extractedType);
      setEmail(extractedEmail || '');
      console.log('Parameters extracted from query:', { token: extractedToken, type: extractedType, email: extractedEmail });
    } else {
      setMessage('Invalid or missing reset token. Please request a new password reset link.');
      setMessageType('error');
    }
  }, [location.search]);

  // Handle setting new password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!password || !confirmPassword) {
      setMessage('Please enter both password fields');
      setMessageType('error');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      setMessageType('error');
      return;
    }

    if (password.length < 6) {
      setMessage('Password must be at least 6 characters long');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('Attempting password reset with token:', token);
      
      // Make request to reset password
      const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          type,
          email,
          password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage('Password updated successfully! Redirecting to login...');
        setMessageType('success');
        // Redirect to login page after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMessage(data.error || 'Failed to reset password. Please try again.');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setMessage('An error occurred. Please try again later.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-center mb-6 text-gray-800">Set New Password</h1>
        
        {message && (
          <div className={`p-3 ${messageType === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'} rounded text-sm text-center mb-4`}>
            {message}
          </div>
        )}
        
        <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="password" className="font-semibold text-gray-600">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              className="p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex flex-col gap-2">
            <label htmlFor="confirmPassword" className="font-semibold text-gray-600">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <button 
            type="submit" 
            className="mt-2 p-3 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors disabled:bg-blue-300"
            disabled={loading || !token}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
          
          <div className="text-center mt-4">
            <a 
              href="/login" 
              className="text-blue-500 hover:text-blue-700 transition-colors"
            >
              Back to Login
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetNewPassword;
